import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1742046335792-060080d72460?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwY3JlYXRpdmUlMjBvZmZpY2V8ZW58MHx8fHwxNzc3MjkyMTAzfDA&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex flex-col justify-end overflow-hidden pt-32 pb-20"
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />

      {/* Top tag */}
      <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8 text-[11px] uppercase tracking-[0.3em] text-white/70"
        >
          <span className="inline-block w-2 h-2 bg-[#FF3B30] animate-pulse" />
          <span>Penang · Kuala Lumpur · Johor</span>
        </motion.div>

        {/* Heading */}
        <div className="max-w-[1100px]">
          <motion.h1
            data-testid="hero-heading"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="display-xxl text-5xl sm:text-6xl md:text-7xl lg:text-[8.5rem]"
          >
            We make <span className="text-[#FF3B30]">brands</span>
            <br />
            impossible <span className="text-outline">to ignore</span>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-10 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed"
          >
            A full‑service media agency delivering videography, photography,
            social, content, design and SEO — built for ambitious Malaysian
            brands that refuse to blend in.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-cta-book"
              onClick={() => scrollTo("book")}
              className="btn-primary inline-flex items-center gap-3 group"
            >
              Book a free consult
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollTo("services")}
              className="btn-ghost inline-flex items-center gap-3"
            >
              <Play size={14} />
              See our services
            </button>
          </motion.div>
        </div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10"
        >
          {[
            { k: "180+", v: "Projects shipped" },
            { k: "3", v: "Malaysian cities" },
            { k: "9", v: "Creative services" },
            { k: "24h", v: "Avg. reply time" },
          ].map((s) => (
            <div
              key={s.v}
              className="bg-black p-6 md:p-8"
              data-testid={`hero-stat-${s.v.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="font-display text-3xl md:text-4xl font-black">
                {s.k}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.2em] text-white/50">
                {s.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
